import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { RSI_OVERBOUGHT, RSI_OVERSOLD, VOLUME_SURGE_THRESHOLD } from "@/lib/constants";
import TopBar from "@/components/TopBar";
import { Num, ScoreBar } from "@/components/DataBits";
import { Plus, X, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import Footer from "@/components/Footer";

function rsiClass(rsi) {
  if (rsi >= RSI_OVERBOUGHT) return "neg";
  if (rsi <= RSI_OVERSOLD) return "pos";
  return "";
}

export default function Watchlist() {
  const nav = useNavigate();
  const [symbols, setSymbols] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSym, setNewSym] = useState("");

  const loadRows = useCallback(async (syms) => {
    if (syms.length === 0) { setRows([]); return; }
    const r = await api.get(`/momentum?symbols=${syms.join(",")}`);
    setRows(r.data.rows);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const w = await api.get("/watchlist");
      setSymbols(w.data.symbols);
      await loadRows(w.data.symbols);
    } catch {
      toast.error("Load failed");
    } finally { setLoading(false); }
  }, [loadRows]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const s = newSym.trim().toUpperCase();
    if (!s) return;
    try {
      const r = await api.post("/watchlist", { symbol: s });
      setSymbols(r.data.symbols);
      setNewSym("");
      await loadRows(r.data.symbols);
      toast.success(`${s} added`);
    } catch { toast.error("Add failed"); }
  };

  const remove = async (s) => {
    try {
      const r = await api.delete(`/watchlist/${s}`);
      setSymbols(r.data.symbols);
      await loadRows(r.data.symbols);
    } catch { toast.error("Remove failed"); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopBar />
      <div className="px-6 py-5">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="micro-label">/ user watchlist</div>
            <h1 className="font-heading text-3xl mt-1">My Tickers</h1>
          </div>
          <form onSubmit={add} className="flex gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-[#222] bg-[#0f0f0f]">
              <MagnifyingGlass size={14} className="text-[#555]" />
              <input
                placeholder="ADD TICKER (e.g. AAPL)"
                value={newSym}
                onChange={e=>setNewSym(e.target.value.toUpperCase())}
                className="bg-transparent outline-none text-xs font-mono-data placeholder:text-[#555] w-44 uppercase"
                data-testid="watchlist-add-input"
              />
            </div>
            <button type="submit" className="flex items-center gap-1 px-3 py-1.5 bg-white text-black hover:bg-[#00e676] text-xs font-mono-data uppercase tracking-wider"
              data-testid="watchlist-add-btn">
              <Plus size={14} weight="bold"/> ADD
            </button>
          </form>
        </div>

        <div className="panel">
          <table className="w-full text-sm" data-testid="watchlist-table">
            <thead className="bg-[#0a0a0a] border-b border-[#222]">
              <tr>
                <th className="px-3 py-2 micro-label text-left">SYMBOL</th>
                <th className="px-3 py-2 micro-label text-right">PRICE</th>
                <th className="px-3 py-2 micro-label text-right">1D %</th>
                <th className="px-3 py-2 micro-label text-right">5D %</th>
                <th className="px-3 py-2 micro-label text-right">RSI</th>
                <th className="px-3 py-2 micro-label text-right">VOL ×</th>
                <th className="px-3 py-2 micro-label text-left">SCORE</th>
                <th className="px-3 py-2 micro-label text-center w-12">×</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="text-center py-10 font-mono-data text-xs text-[#555]">LOADING…</td></tr>}
              {!loading && symbols.length === 0 && (
                <tr><td colSpan="8" className="text-center py-10 font-mono-data text-xs text-[#555]">WATCHLIST EMPTY — ADD A TICKER</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.symbol} onClick={()=>nav(`/stock/${r.symbol}`)}
                  className="data-row border-b border-[#1a1a1a] cursor-pointer"
                  data-testid={`watchlist-row-${r.symbol}`}>
                  <td className="px-3 py-2 font-mono-data font-semibold">{r.symbol}</td>
                  <td className="px-3 py-2 text-right"><Num value={r.price} /></td>
                  <td className="px-3 py-2 text-right"><Num value={r.change_1d} signed suffix="%" /></td>
                  <td className="px-3 py-2 text-right"><Num value={r.change_5d} signed suffix="%" /></td>
                  <td className="px-3 py-2 text-right">
                    <span className={`font-mono-data ${rsiClass(r.rsi)}`}>{r.rsi.toFixed(1)}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`font-mono-data ${r.volume_surge >= VOLUME_SURGE_THRESHOLD ? "warn" : ""}`}>{r.volume_surge.toFixed(2)}×</span>
                  </td>
                  <td className="px-3 py-2"><ScoreBar score={r.momentum_score}/></td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={(e)=>{e.stopPropagation(); remove(r.symbol);}}
                      className="text-[#8a8a8e] hover:text-[#ff3b30]"
                      data-testid={`watchlist-remove-${r.symbol}`}>
                      <X size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}
